import FilterIcon from "@atlaskit/icon/core/filter";
import SearchIcon from "@atlaskit/icon/core/search";
import {useQuery} from "@tanstack/react-query";
import {useMemo, useState, type ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {BottomDrawer} from "~/components/BottomDrawer";
import {OttopilotColumnType, type OttopilotColumn} from "~/components/ottopilotDataViewer/column";
// Previous inline calendar range pill UI (desktop-style DateRangePill in mobile drawer).
// import {
//     applyColumnDateRangeToFilters,
//     FilterColumnDateRangePill,
//     readColumnDateRangeFromFilters,
// } from "~/components/ottopilotDataViewer/filterColumnDateRangePill";
import {MobileFilterScheduleStyleDateColumnPanel} from "~/components/ottopilotDataViewer/mobileFilterDateColumnPanel";
import {filterConfigurationExcludingColumn, type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {NULL_SENTINEL, VirtualizedFilterList} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {FilterSelectAllRow} from "~/components/ottopilotDataViewer/filterSelectAllRow";
import {
    buildFilterColumnValuesForPicker,
    buildFilterDisplayLabelIndex,
    countSelectedFilterPickerValues,
    excludeValuesFromFilterPicker,
    isFilterPickerValueSelected,
    normalizeFilterNullishRawValues,
    resolveSelectAllEphemeralLineItems,
    toggleFilterValuesByDisplayLabel,
} from "~/components/ottopilotDataViewer/filterValueDisplayGrouping";
import {sanitizeCreatedUpdatedByFilterColumnValues} from "~/utilities/deprecatedSystemGeneratedCreatedUpdatedByUserIds";
import {
    getDataViewerDateRangeColumnId,
    cloneFilterConfiguration,
    countActiveDrawerColumnFilters,
    getCategorySelectionCount,
    getDrawerFilterColumnIds,
    getUpfrontVisibleColumnIds,
    removeColumnFilters,
    retainNonUserAppliedDrawerFilters,
} from "~/components/ottopilotDataViewer/mobileFilterDrawerHelpers";
import {filterLineItemsEqual} from "~/components/ottopilotDataViewer/filterLineItemEquality";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {AnankeBadge} from "@vesta/ananke/components/anankeBadge";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeTextField} from "@vesta/ananke/components/anankeTextField";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

function MobileFilterInColumnPanel<T extends {id: Uuid}>({
    controller,
    column,
    filterConfiguration,
    onFilterConfigurationChange,
}: {
    controller: OttopilotDataViewerController<T>;
    column: OttopilotColumn<T>;
    filterConfiguration: FilterConfiguration;
    onFilterConfigurationChange: (next: FilterConfiguration) => void;
}) {
    const appliedFilterLineItem = filterConfiguration.lineItems.find(
        (lineItem): lineItem is Extract<typeof lineItem, {operation: FilterOperation.in}> =>
            lineItem.columnId === column.id && lineItem.operation === FilterOperation.in,
    );

    const [searchValue, setSearchValue] = useState("");

    const filterConfigurationForColumnValues = filterConfigurationExcludingColumn(
        filterConfiguration,
        column.id,
    );

    const columnValuesQuery = useQuery<Array<string>>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "columns",
            column.id,
            "values",
            "mobileDrawer",
            filterConfigurationForColumnValues.revisionId,
            JSON.stringify(filterConfigurationForColumnValues.lineItems),
            controller.searchConfiguration.revisionId,
        ],
        queryFn:
            column.filteringCapability_in?.valuesQueryFn !== undefined
                ? () =>
                      column.filteringCapability_in!.valuesQueryFn!({
                          filterConfiguration: filterConfigurationForColumnValues,
                      })
                : () =>
                      controller.columnValuesQueryFn({
                          columnId: column.id,
                          filterConfiguration: filterConfigurationForColumnValues,
                          searchConfiguration: controller.searchConfiguration,
                      }),
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: "always",
    });

    const columnValuesQueryResult =
        columnValuesQuery.data !== undefined
            ? sanitizeCreatedUpdatedByFilterColumnValues(column.id, columnValuesQuery.data)
            : undefined;
    const excludePickerValues = column.filteringCapability_in?.excludePickerValues;
    const builtPickerValues =
        columnValuesQueryResult !== undefined
            ? buildFilterColumnValuesForPicker({
                  queryValues: columnValuesQueryResult,
                  appliedFilterValues: appliedFilterLineItem?.values,
                  nullSentinel: NULL_SENTINEL,
                  valueFormatter:
                      column.valueFormatter !== undefined
                          ? (value) => String(column.valueFormatter!(value as never))
                          : undefined,
              })
            : {allRawValues: [], pickerValues: []};
    const allColumnRawValuesForPicker = excludeValuesFromFilterPicker(
        builtPickerValues.allRawValues,
        excludePickerValues,
    );
    const columnValuesForPicker = excludeValuesFromFilterPicker(
        builtPickerValues.pickerValues,
        excludePickerValues,
    );
    const filterValueFormatter =
        column.valueFormatter !== undefined
            ? (value: string) => String(column.valueFormatter!(value as never))
            : undefined;

    const ephemeralValues =
        appliedFilterLineItem !== undefined && appliedFilterLineItem.values !== undefined
            ? normalizeFilterNullishRawValues(appliedFilterLineItem.values, NULL_SENTINEL)
            : null;

    const ephemeralSelectedSet = useMemo(() => {
        if (ephemeralValues === null) {
            return null;
        }
        return new Set(ephemeralValues);
    }, [ephemeralValues]);
    const displayLabelIndex = useMemo(() => {
        if (filterValueFormatter === undefined) {
            return null;
        }
        return buildFilterDisplayLabelIndex(allColumnRawValuesForPicker, filterValueFormatter);
    }, [filterValueFormatter, allColumnRawValuesForPicker]);

    const isPickerValueSelected = (columnValue: string): boolean => {
        return isFilterPickerValueSelected({
            columnValue: columnValue,
            selectedValues: ephemeralValues,
            selectedValuesSet: ephemeralSelectedSet,
            valueFormatter: filterValueFormatter,
            displayLabelIndex: displayLabelIndex,
            nullSentinel: NULL_SENTINEL,
        });
    };

    const setEphemeralValues = (lineItems: Array<string> | null) => {
        if (column.customFilter !== undefined) {
            void Promise.resolve(
                column.customFilter({
                    controller: {
                        setFilterConfiguration: (updater) => {
                            onFilterConfigurationChange(updater(filterConfiguration));
                        },
                    },
                    ephemeralValue: {
                        revisionId: generateUuid(),
                        lineItems,
                    },
                    column: {
                        id: column.id,
                        displayName: column.displayName,
                    },
                    closePopup: () => {},
                }),
            );
            return;
        }
        onFilterConfigurationChange(
            lineItems === null
                ? {
                      revisionId: generateUuid(),
                      lineItems: filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId !== column.id),
                  }
                : {
                      revisionId: generateUuid(),
                      lineItems: [
                          ...filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId !== column.id),
                          {
                              columnId: column.id,
                              operation: FilterOperation.in,
                              values: normalizeFilterNullishRawValues(lineItems, NULL_SENTINEL),
                          },
                      ],
                  },
        );
    };

    const filteredColumnValues = useMemo(() => {
        if (columnValuesForPicker.length === 0) {
            return [];
        }
        if (searchValue.trim() === "") {
            return columnValuesForPicker;
        }
        const searchLower = searchValue.toLowerCase().trim();
        return columnValuesForPicker.filter((columnValue) => {
            if (columnValue === NULL_SENTINEL) {
                return "null".includes(searchLower);
            }
            const displayValue =
                column.valueFormatter !== undefined
                    ? String(column.valueFormatter(columnValue as never))
                    : String(columnValue);
            return displayValue.toLowerCase().includes(searchLower);
        });
    }, [columnValuesForPicker, searchValue, column.valueFormatter]);

    const selectedFilteredPickerCount = useMemo(() => {
        return countSelectedFilterPickerValues({
            pickerValues: filteredColumnValues,
            selectedValues: ephemeralValues,
            selectedValuesSet: ephemeralSelectedSet,
            valueFormatter: filterValueFormatter,
            displayLabelIndex: displayLabelIndex,
            nullSentinel: NULL_SENTINEL,
        });
    }, [
        filteredColumnValues,
        ephemeralValues,
        ephemeralSelectedSet,
        filterValueFormatter,
        displayLabelIndex,
    ]);

    const renderColumnValue = (columnValue: string): ReactNode => {
        if (columnValue === NULL_SENTINEL || columnValue.trim() === "" || columnValue === "-") {
            return "NULL";
        }
        if (column.valueFormatter !== undefined) {
            const formatted = column.valueFormatter(columnValue as never);
            if (formatted === null || formatted === undefined) {
                return "NULL";
            }
            const formattedText = String(formatted);
            if (formattedText.trim() === "" || formattedText === "-") {
                return "NULL";
            }
            return formatted;
        }
        return columnValue;
    };

    const onItemClick = (columnValue: string) => {
        if (filterValueFormatter === undefined) {
            if (ephemeralValues === null) {
                setEphemeralValues(
                    columnValuesForPicker.filter((value) => value !== columnValue),
                );
                return;
            }
            const wasPreviouslySelected = ephemeralValues.includes(columnValue);
            if (wasPreviouslySelected === true) {
                setEphemeralValues(ephemeralValues.filter((value) => value !== columnValue));
            } else {
                setEphemeralValues([...ephemeralValues, columnValue]);
            }
            return;
        }
        setEphemeralValues(
            toggleFilterValuesByDisplayLabel(
                columnValue,
                ephemeralValues,
                allColumnRawValuesForPicker,
                filterValueFormatter,
                NULL_SENTINEL,
            ),
        );
    };

    if (columnValuesQuery.status === "pending") {
        return <div className="px-ds-space-150 py-ds-space-100 text-ds-text-subtle">Loading</div>;
    }

    if (columnValuesQuery.status === "error") {
        return (
            <div className="px-ds-space-150 py-ds-space-100 text-ds-text-danger">
                Error: {columnValuesQuery.error.message}
            </div>
        );
    }

    if (columnValuesQueryResult === undefined || columnValuesQueryResult.length === 0) {
        return <div className="px-ds-space-150 py-ds-space-100 text-ds-text-subtle">No values found</div>;
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="px-ds-space-100 pt-ds-space-050 pb-ds-space-100">
                <AnankeTextField
                    id={`mobile-filter-search-${column.id}`}
                    value={searchValue}
                    onChange={(e) => {
                        setSearchValue(e.currentTarget.value);
                    }}
                    placeholder="Search"
                    elemBeforeInput={
                        <div className="pl-ds-space-100 grid place-items-center">
                            <SearchIcon label="" />
                        </div>
                    }
                />
            </div>
            <FilterSelectAllRow
                className="px-ds-space-150 py-ds-space-100 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-ds-space-150 border-b border-ds-border"
                isChecked={ephemeralValues === null ? true : ephemeralValues.length > 0}
                isIndeterminate={
                    ephemeralValues === null
                        ? false
                        : selectedFilteredPickerCount > 0 &&
                          selectedFilteredPickerCount < filteredColumnValues.length
                }
                onChange={(e) => {
                    if (e.target.checked === true) {
                        setEphemeralValues(
                            resolveSelectAllEphemeralLineItems({
                                searchQuery: searchValue,
                                filteredPickerValues: filteredColumnValues,
                                allRawValues: allColumnRawValuesForPicker,
                                previousLineItems: ephemeralValues,
                                valueFormatter: filterValueFormatter,
                                nullSentinel: NULL_SENTINEL,
                            }),
                        );
                    } else {
                        setEphemeralValues([]);
                    }
                }}
            />
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
                {filteredColumnValues.length === 0 ? (
                    <div className="px-ds-space-150 py-ds-space-100 text-ds-text-subtle">
                        No matching values found
                    </div>
                ) : (
                    <VirtualizedFilterList
                        items={filteredColumnValues}
                        isSelected={isPickerValueSelected}
                        onItemClick={onItemClick}
                        renderItem={(columnValue) => renderColumnValue(columnValue)}
                    />
                )}
            </div>
        </div>
    );
}

function MobileFilterDateColumnPanel<T extends {id: Uuid}>({
    column,
    filterConfiguration,
    onFilterConfigurationChange,
}: {
    column: OttopilotColumn<T>;
    filterConfiguration: FilterConfiguration;
    onFilterConfigurationChange: (next: FilterConfiguration) => void;
}) {
    return (
        <MobileFilterScheduleStyleDateColumnPanel
            column={column}
            filterConfiguration={filterConfiguration}
            onFilterConfigurationChange={onFilterConfigurationChange}
        />
    );

    // Previous UI: inline DateRangePill calendar picker (commented out — use ScheduleCallModal-style fields above).
    // const dateRange = readColumnDateRangeFromFilters(filterConfiguration.lineItems, column.id);
    // return (
    //     <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain px-ds-space-150 py-ds-space-100">
    //         <FilterColumnDateRangePill
    //             displayName={column.displayName}
    //             value={dateRange}
    //             onChange={(range) => {
    //                 onFilterConfigurationChange(applyColumnDateRangeToFilters(filterConfiguration, range, column.id));
    //             }}
    //             matchFilterPills={true}
    //             shouldRenderToParent={false}
    //         />
    //     </div>
    // );
}

export function MobileDataViewerFilterDrawer<T extends {id: Uuid}>({
    controller,
    isCalendarView,
}: {
    controller: OttopilotDataViewerController<T>;
    isCalendarView: boolean;
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null);
    const [ephemeralFilterConfiguration, setEphemeralFilterConfiguration] = useState<FilterConfiguration>(
        () => cloneFilterConfiguration(controller.filterConfiguration),
    );

    if (isCalendarView === true) {
        return null;
    }

    const drawerColumnIds = getDrawerFilterColumnIds(controller);

    if (drawerColumnIds.length === 0) {
        return null;
    }

    const activeFilterCount = countActiveDrawerColumnFilters(controller);

    const openDrawer = () => {
        const nextFilters = cloneFilterConfiguration(controller.filterConfiguration);
        setEphemeralFilterConfiguration(nextFilters);
        setSelectedColumnId(drawerColumnIds[0] ?? null);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
    };

    const selectedColumn =
        selectedColumnId === null
            ? undefined
            : controller.columns.lineItems.find((column) => column.id === selectedColumnId);

    const resetFilters = () => {
        const drawerColumnIdSet = new Set(drawerColumnIds);
        const preservedDrawerLineItems = retainNonUserAppliedDrawerFilters(
            ephemeralFilterConfiguration,
            controller.filterClearConfig,
            controller.journeyScopeFilterConfig,
        ).filter((lineItem) => drawerColumnIdSet.has(lineItem.columnId) === true);

        let nextFilters = cloneFilterConfiguration(ephemeralFilterConfiguration);
        for (const columnId of drawerColumnIds) {
            nextFilters = removeColumnFilters(nextFilters, columnId);
        }
        setEphemeralFilterConfiguration({
            revisionId: generateUuid(),
            lineItems: [...nextFilters.lineItems, ...preservedDrawerLineItems],
        });
    };

    const applyFilters = () => {
        const upfrontColumnIds = getUpfrontVisibleColumnIds(controller);
        const dateRangeColumnId = getDataViewerDateRangeColumnId(controller);
        const upfrontLineItems = controller.filterConfiguration.lineItems.filter(
            (lineItem) =>
                upfrontColumnIds.has(lineItem.columnId) === true || lineItem.columnId === dateRangeColumnId,
        );
        const drawerLineItems = ephemeralFilterConfiguration.lineItems.filter(
            (lineItem) =>
                upfrontColumnIds.has(lineItem.columnId) === false &&
                lineItem.columnId !== dateRangeColumnId,
        );
        const preservedBaselines = retainNonUserAppliedDrawerFilters(
            controller.filterConfiguration,
            controller.filterClearConfig,
            controller.journeyScopeFilterConfig,
        );
        const mergedLineItems = [...upfrontLineItems, ...drawerLineItems];
        for (const preserved of preservedBaselines) {
            if (
                mergedLineItems.some((lineItem) => filterLineItemsEqual(lineItem, preserved) === true) !== true
            ) {
                mergedLineItems.push(preserved);
            }
        }
        controller.setFilterConfiguration({
            revisionId: generateUuid(),
            lineItems: mergedLineItems,
        });
        closeDrawer();
    };

    const drawerFooter = (
        <div className="py-ds-space-100 grid grid-cols-[auto_auto] justify-end items-center gap-x-ds-space-100">
            <AnankeButton type="button" appearance="subtle" onClick={resetFilters}>
                Reset
            </AnankeButton>
            <AnankeButton type="button" appearance="primary" onClick={applyFilters}>
                Apply
            </AnankeButton>
        </div>
    );

    return (
        <>
            <div className="relative inline-grid shrink-0">
                <AnankeIconButton
                    type="button"
                    appearance="default"
                    icon={FilterIcon}
                    label="Filter"
                    aria-expanded={drawerOpen}
                    isSelected={drawerOpen === true || activeFilterCount > 0}
                    onClick={() => {
                        if (drawerOpen === false) {
                            openDrawer();
                        } else {
                            closeDrawer();
                        }
                    }}
                />
                {activeFilterCount > 0 ? (
                    <div className="absolute -top-1 -right-1 pointer-events-none">
                        <AnankeBadge appearance="primary">{activeFilterCount}</AnankeBadge>
                    </div>
                ) : null}
            </div>

            <BottomDrawer
                open={drawerOpen}
                setOpen={(value) => {
                    const next = typeof value === "function" ? value(drawerOpen) : value;
                    if (next === false) {
                        closeDrawer();
                    } else {
                        setDrawerOpen(true);
                    }
                }}
                title="Filter"
                zIndex={100}
                fitContentOnOpen={false}
                initialOpenHeight={520}
                headerBottomBorder={true}
                lockContentScroll={true}
                footer={drawerFooter}
            >
                <div className="grid h-full min-h-0 grid-cols-[minmax(7.5rem,35%)_minmax(0,1fr)] overflow-hidden">
                    <div className="min-h-0 overflow-y-auto overscroll-y-contain border-r border-ds-border bg-ds-background-neutral-subtle">
                        <ItemBuilder
                            items={drawerColumnIds}
                            itemBuilder={(columnId) => {
                                const column = controller.columns.lineItems.find((item) => item.id === columnId);
                                if (column === undefined) {
                                    return null;
                                }
                                const isSelected = selectedColumnId === column.id;
                                const selectionCount = getCategorySelectionCount(column, ephemeralFilterConfiguration, {
                                    filterClearConfig: controller.filterClearConfig,
                                    journeyScopeFilterConfig: controller.journeyScopeFilterConfig,
                                });
                                return (
                                    <button
                                        key={column.id}
                                        type="button"
                                        onClick={() => {
                                            setSelectedColumnId(column.id);
                                        }}
                                        className={twJoin(
                                            "w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-ds-space-050",
                                            "px-ds-space-100 py-ds-space-150 text-left text-sm",
                                            isSelected === true
                                                ? "bg-ds-background-selected text-ds-text-accent-blue font-medium"
                                                : "text-ds-text hover:bg-ds-background-neutral-hovered",
                                        )}
                                    >
                                        <span className="truncate">{column.displayName}</span>
                                        {selectionCount > 0 ? (
                                            <AnankeBadge appearance="primary">{selectionCount}</AnankeBadge>
                                        ) : (
                                            <span />
                                        )}
                                    </button>
                                );
                            }}
                        />
                    </div>
                    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-ds-surface">
                        {selectedColumn === undefined ? (
                            <div className="h-full min-h-0 overflow-y-auto overscroll-y-contain px-ds-space-150 py-ds-space-100 text-ds-text-subtle text-sm">
                                Select a filter
                            </div>
                        ) : selectedColumn.type === OttopilotColumnType.iso8601DateTime ? (
                            <MobileFilterDateColumnPanel
                                column={selectedColumn}
                                filterConfiguration={ephemeralFilterConfiguration}
                                onFilterConfigurationChange={setEphemeralFilterConfiguration}
                            />
                        ) : (
                            <MobileFilterInColumnPanel
                                controller={controller}
                                column={selectedColumn}
                                filterConfiguration={ephemeralFilterConfiguration}
                                onFilterConfigurationChange={setEphemeralFilterConfiguration}
                            />
                        )}
                    </div>
                </div>
            </BottomDrawer>
        </>
    );
}
